"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useInstantSearchPickupPointsQuery = void 0;
var _environment = require("@ori/environment");
var _globalApiClientReact = require("@ori/global-api-client-react");
var _i18n = require("@ori/i18n");
var _constants = require("../constants");
var _swrConfig = require("../constants/swrConfig");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _checkoutDeliveryContext = require("../contexts/checkoutDeliveryContext");
var _useGeocodingSearch = require("../hooks/useGeocodingSearch");
var _logger = require("../logger");
var _models = require("../models");
var _utils = require("../utils");
const hookName = 'useInstantSearchPickupPointsQuery';
const getDeliveryChannelsWithoutHomeDelivery = data => data ? data.filter(item => item.deliveryMethod !== _models.DeliveryMethod.Home) : undefined;

/**
 * @obsolete Can be dropped later when flat selection UI is used in all markets
 * and/or the merged map & list UI switch is enabled everywhere.
 */
const useInstantSearchPickupPointsQuery = options => {
  (0, _logger.logDebug)(`${hookName} hook called, params:`, options);
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    maxDisplayedSearchResults
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  const headers = (0, _utils.getHeaders)({
    currentLanguage: language,
    tenant
  });
  const {
    sessionId = ''
  } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
  const getSwrOptions = (description, enabled) => ({
    swr: {
      enabled,
      onSuccess: data => (0, _logger.logInfo)(`${hookName} hook - ${description}: loaded data:`, data),
      ..._swrConfig.errorHandlingSwrOptions
    },
    request: {
      timeout: 10 * 1000
    } // 10 sec timeout
  });
  const {
    filter,
    isActive,
    isMapEnabled,
    isMapLayoutShown,
    ...baseQueryParams
  } = options;
  const {
    searchTerm
  } = baseQueryParams;
  const isSearchTermSet = Boolean(searchTerm);

  // base query - run only when searchTerm is set
  const {
    data: baseQueryResult,
    error: baseQueryError,
    isValidating: isBaseQueryLoading
  } = (0, _globalApiClientReact.useGetShoppingSessionDeliveryChannels)(sessionId, {
    filter,
    searchTerm,
    skip: 0,
    top: maxDisplayedSearchResults
  }, headers, getSwrOptions('Base data query', isActive && isSearchTermSet));
  const {
    collection: rawBaseQueryData
  } = baseQueryResult ?? {};
  const baseQueryData = getDeliveryChannelsWithoutHomeDelivery(rawBaseQueryData);
  const isEmptyBaseQueryResult = isSearchTermSet && Array.isArray(baseQueryData) && baseQueryData.length === 0;
  (0, _logger.logDebug)(`${hookName} hook - base query data result:`, {
    baseQueryResult,
    baseQueryData,
    isEmptyBaseQueryResult
  });

  /*
  For now enabling geocoding search fallback only in list search. For map, we would have
  to switch off filtering by the text not found in the base data query, which brings other considerations.
  */
  const isGeocodingSearchEnabled = isSearchTermSet && isMapEnabled && isEmptyBaseQueryResult && !isMapLayoutShown;

  // query to get coordinates for fallback search - run when searchTerm is set and base query returned 0 results
  const geocoding = (0, _useGeocodingSearch.useGeocodingSearch)({
    isActive: isActive && isGeocodingSearchEnabled,
    searchTerm
  });
  const isFallbackQueryEnabled = isEmptyBaseQueryResult && Boolean(geocoding.data) && !isMapLayoutShown;

  // fallback search query for pickup points near the received coordinates - run when coordinates are defined
  const {
    data: {
      collection: rawFallbackQueryData
    } = {},
    error: fallbackQueryError,
    isValidating: isFallbackQueryLoading
  } = (0, _globalApiClientReact.useGetShoppingSessionDeliveryChannels)(sessionId,
  /*
  searchTerm not filled-in, as it would find nothing as in the base query
  10km radius set for now; this could be dynamic according to shown map area or similar
  */
  {
    filter,
    radius: _constants.SEARCH_RADIUS,
    ...geocoding.data,
    skip: 0,
    top: maxDisplayedSearchResults
  }, headers, getSwrOptions('Fallback data query', isActive && isFallbackQueryEnabled));
  const fallbackQueryData = getDeliveryChannelsWithoutHomeDelivery(rawFallbackQueryData);
  const isInstantResultsLoading = isBaseQueryLoading || geocoding.isLoading || isFallbackQueryLoading;
  const finalBaseQueryData = baseQueryData ?? [];
  const instantResults = finalBaseQueryData.length ? finalBaseQueryData : fallbackQueryData ?? [];
  const instantResultsError = baseQueryError ?? geocoding.error ?? fallbackQueryError;
  return {
    foundPlaceLabel: geocoding.data?.label,
    foundPosition: geocoding.data ? {
      latitude: geocoding.data.latitude,
      longitude: geocoding.data.longitude
    } : undefined,
    isInstantResultsLoading,
    instantResults,
    instantResultsError
  };
};
exports.useInstantSearchPickupPointsQuery = useInstantSearchPickupPointsQuery;